import { findSkin } from "./skins";
import type { ColorScheme, FormEntry, LayoutEntry, PaletteEntry } from "./types";

/**
 * The registry is the single source of truth for which appearances exist.
 *
 * Convention, relied on by styles.ts and by the CSS: an entry's `id` is exactly
 * the stylesheet filename AND exactly the `data-*` attribute value. Adding an
 * appearance is one new CSS file plus one line here — stylesheets are picked up
 * automatically and no React component changes.
 *
 * Palettes are named by character, never by colour: a palette called "dark"
 * cannot later become a warm charcoal without the name turning into a lie.
 */

export const PALETTES: readonly PaletteEntry[] = [
  { id: "paper", nameEn: "Paper", nameAr: "ورق", mode: "light", availableOn: "universal" },
  { id: "steel", nameEn: "Steel", nameAr: "فولاذ", mode: "light", availableOn: "universal" },
  { id: "ember", nameEn: "Ember", nameAr: "جمر", mode: "dark", availableOn: "universal" },
  { id: "graphite", nameEn: "Graphite", nameAr: "غرافيت", mode: "dark", availableOn: "universal" },

  // Skin-specific: authored for one skin's character and offered nowhere else.
  { id: "phosphor", nameEn: "Phosphor", nameAr: "فوسفور", mode: "dark", availableOn: ["board"] },
  { id: "engraved", nameEn: "Engraved", nameAr: "محفور", mode: "light", availableOn: ["intaglio"] },
  { id: "thermal", nameEn: "Thermal", nameAr: "حراري", mode: "light", availableOn: ["receipt"] },
];

export const FORMS: readonly FormEntry[] = [
  { id: "ledger", nameEn: "Ledger", nameAr: "دفتر" },
  { id: "board", nameEn: "Board", nameAr: "لوحة" },
  { id: "counter", nameEn: "Counter", nameAr: "شبّاك" },
  { id: "intaglio", nameEn: "Intaglio", nameAr: "نقش" },
  { id: "slate", nameEn: "Slate", nameAr: "لوح" },
  { id: "receipt", nameEn: "Receipt", nameAr: "إيصال" },
];

export const LAYOUTS: readonly LayoutEntry[] = [
  { id: "stack", nameEn: "Stack", nameAr: "مكدّس" },
  { id: "compact", nameEn: "Compact", nameAr: "مضغوط" },
  { id: "grid", nameEn: "Grid", nameAr: "شبكة" },
];

export const DEFAULT_PALETTE = "paper";
export const DEFAULT_FORM = "ledger";
export const DEFAULT_LAYOUT = "stack";

export function isKnownPalette(id: string): boolean {
  return PALETTES.some((p) => p.id === id);
}

export function isKnownForm(id: string): boolean {
  return FORMS.some((f) => f.id === id);
}

export function isKnownLayout(id: string): boolean {
  return LAYOUTS.some((l) => l.id === id);
}

export function findPalette(id: string): PaletteEntry | undefined {
  return PALETTES.find((p) => p.id === id);
}

/** True when the palette is offered on the given skin. */
export function isPaletteAvailableOn(palette: PaletteEntry, skinId: string): boolean {
  return palette.availableOn === "universal" || palette.availableOn.includes(skinId);
}

export interface OfferedPalette {
  readonly entry: PaletteEntry;
  /** Authored for this skin in particular, rather than universal. */
  readonly specific: boolean;
}

/**
 * The palettes a skin offers, universal ones first.
 *
 * Ordering is part of the design: the gallery distinguishes specific palettes
 * from universal ones by position plus a halo ring, so the two groups must
 * arrive already separated rather than being sorted in the view.
 */
export function palettesForSkin(skinId: string): readonly OfferedPalette[] {
  const offered = PALETTES.filter((p) => isPaletteAvailableOn(p, skinId)).map((entry) => ({
    entry,
    specific: entry.availableOn !== "universal",
  }));
  return [...offered.filter((o) => !o.specific), ...offered.filter((o) => o.specific)];
}

/** Colour scheme for a palette id, falling back rather than throwing. */
export function paletteMode(id: string): ColorScheme {
  const found = findPalette(id);
  if (found) return found.mode;
  const fallback = findPalette(DEFAULT_PALETTE);
  return fallback ? fallback.mode : "light";
}

/**
 * Sanitises a palette against a skin. A stored pairing can become invalid when
 * a palette's availability is narrowed in a later release, so this is the one
 * place that decides what happens: fall back to the skin's own default rather
 * than showing a combination its author never approved.
 */
export function resolvePaletteForSkin(paletteId: string, skinId: string): string {
  const palette = findPalette(paletteId);
  if (palette && isPaletteAvailableOn(palette, skinId)) return paletteId;
  return findSkin(skinId)?.defaultPalette ?? DEFAULT_PALETTE;
}
